import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import to from "await-to-js";

import { TReducerError } from "@/types/app";

import handlingError from "@/utils/handlingError";

import {
  TAppointmentRequestStatus,
  TPagination,
} from "@/features/backoffice/types";

import { TAppointmentSlotInitialValues } from "@/features/backoffice/containers/Muqabalas/AppointmentSlots/AppointmentSlots.container";
import {
  IAPIDeleteAppointmentSlot,
  IAPIGetAppointmentSlot,
  IAPIGetAppointmentSlots,
  IAPIPatchAppointmentSlot,
  IAPIPostAppointmentSlot,
} from "@/features/backoffice/types/interfaces/api/appointmentSlots";
import {
  IAPIGetMuqabala,
  IAPIGetMuqabalas,
  IAPIPatchCancelReportMuqabala,
} from "@/features/backoffice/types/interfaces/api/muqabalas";
import {
  getAppointmentRequests,
  getAppointmentRequest,
  cancelReportMuqabala,
  getAppointmentSlots,
  getAppointmentSlot,
  createAppointmentSlot,
  editAppointmentSlot,
  deleteAppointmentSlot,
} from "@/features/backoffice/api/muqabalas";

export const getAppointmentRequestsAsync = createAsyncThunk<
  IAPIGetMuqabalas,
  (TPagination & { status?: TAppointmentRequestStatus }) | undefined,
  {
    rejectValue: TReducerError;
  }
>(
  "muqabala/getAppointmentRequests",
  async (pagination, { rejectWithValue }) => {
    const [error, data] = await to<IAPIGetMuqabalas, AxiosError<any>>(
      getAppointmentRequests(pagination)
    );

    if (error) {
      return rejectWithValue(handlingError(error));
    }

    return data;
  }
);

export const getAppointmentRequestDetailsAsync = createAsyncThunk<
  IAPIGetMuqabala,
  string,
  {
    rejectValue: TReducerError;
  }
>(
  "muqabala/getAppointmentRequest",
  async (appointmentRequestId, { rejectWithValue }) => {
    const [error, data] = await to<IAPIGetMuqabala, AxiosError<any>>(
      getAppointmentRequest(appointmentRequestId)
    );

    if (error) {
      return rejectWithValue(handlingError(error));
    }

    return data;
  }
);

export const cancelReportMuqabalaAsync = createAsyncThunk<
  IAPIPatchCancelReportMuqabala,
  {
    id: string;
    appointmentSlotId: string;
    reason: string;
    action: "REPORTED" | "CANCELLED";
  },
  {
    rejectValue: TReducerError;
  }
>("muqabala/cancelReportMuqabala", async (datas, { rejectWithValue }) => {
  const [error, data] = await to<
    IAPIPatchCancelReportMuqabala,
    AxiosError<any>
  >(cancelReportMuqabala(datas));

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});

export const getAppointmentSlotsAsync = createAsyncThunk<
  IAPIGetAppointmentSlots,
  string,
  {
    rejectValue: TReducerError;
  }
>("appointmentSlots/get", async (date, { rejectWithValue }) => {
  const [error, data] = await to<IAPIGetAppointmentSlots, AxiosError<any>>(
    getAppointmentSlots(date)
  );

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});

export const getAppointmentSlotAsync = createAsyncThunk<
  IAPIGetAppointmentSlot,
  string,
  {
    rejectValue: TReducerError;
  }
>("appointmentSlot/get", async (appointmentSlotId, { rejectWithValue }) => {
  const [error, data] = await to<IAPIGetAppointmentSlot, AxiosError<any>>(
    getAppointmentSlot(appointmentSlotId)
  );

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});

export const createAppointmentSlotAsync = createAsyncThunk<
  IAPIPostAppointmentSlot,
  TAppointmentSlotInitialValues,
  {
    rejectValue: TReducerError;
  }
>("appointmentSlots/create", async (values, { rejectWithValue }) => {
  const [error, data] = await to<IAPIPostAppointmentSlot, AxiosError<any>>(
    createAppointmentSlot(values)
  );

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});

export const updateAppointmentSlotAsync = createAsyncThunk<
  IAPIPatchAppointmentSlot,
  { id: string; datas: Partial<TAppointmentSlotInitialValues> },
  {
    rejectValue: TReducerError;
  }
>("appointmentSlots/update", async (values, { rejectWithValue }) => {
  const [error, data] = await to<IAPIPatchAppointmentSlot, AxiosError<any>>(
    editAppointmentSlot(values.id, values.datas)
  );

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});

export const deleteAppointmentSlotAsync = createAsyncThunk<
  IAPIDeleteAppointmentSlot,
  string,
  {
    rejectValue: TReducerError;
  }
>("appointmentSlots/delete", async (id, { rejectWithValue }) => {
  const [error, data] = await to<IAPIDeleteAppointmentSlot, AxiosError<any>>(
    deleteAppointmentSlot(id)
  );

  if (error) {
    return rejectWithValue(handlingError(error));
  }

  return data;
});
